/** A list of events supported by the custom event lib. */
export enum EventType {
  // FW_READY = 'fw:ready', see eventTypeReady.ts
  FW_ERROR = 'fw:error',

  // FW_PIP_READY = 'fw:pip:ready', // Indicates PIP iframe has been loaded see eventTypeReady.ts
  FW_NAVIGATE = 'fw:navigate', // Navigate to PIP mode

  /** Widget specific custom events */

  /**
   * @public
   * Fired once when widget initialization outcome is known: content ready, empty feed, or error.
   */
  FW_WIDGET_RENDER_OUTCOME = 'fw:widget:render-outcome',

  FW_EMBEDFEED_ERROR = 'fw:embed-feed:error',
  FW_EMBEDFEED_RENDER = 'fw:embed-feed:render',
  FW_EMBEDFEED_CONTENTREADY = 'fw:embed-feed:content-ready',
  /**
   * @public
   * Indicates the autoplaying thumbnail in the embed feed (grid/carousel) has
   * changed, either by user action or automatically (e.g. autoplay to next video).
   */
  FW_EMBEDFEED_CONTENT_CHANGE = 'fw:embed-feed:content-change',
  FW_EMBEDFEED_IMPRESSION = 'fw:embed-feed:impression',
  FW_EMBEDFEED_THUMBNAILLOAD = 'fw:embed-feed:thumbnail-load',
  FW_EMBEDFEED_THUMBNAILCLICK = 'fw:embed-feed:thumbnail-click',
  FW_EMBEDFEED_THUMBNAILHOVER = 'fw:embed-feed:thumbnail-hover',
  FW_EMBEDFEED_CTA_IMPRESSION = 'fw:embed-feed:cta-impression',
  FW_EMBEDFEED_CTA_CLICK = 'fw:embed-feed:cta-click',
  FW_EMBEDFEED_SCROLL_VIDEOS = 'fw:embed-feed:scroll-videos',

  FW_HEROUNIT_RENDER = 'fw:herounit:render',
  FW_HEROUNIT_IMPRESSION = 'fw:herounit:impression',

  FW_STORYBLOCK_RENDER = 'fw:storyblock:render',
  FW_STORYBLOCK_IMPRESSION = 'fw:storyblock:impression',

  FW_ADD_TO_CALENDAR_RENDER = 'fw:add-to-calendar:render',
  FW_ADD_TO_CALENDAR_IMPRESSION = 'fw:add-to-calendar:impression',
  FW_SHARE_RENDER = 'fw:share:render',
  FW_SHARE_IMPRESSION = 'fw:share:impression',

  FW_PLAYER_RENDER = 'fw:player:render',
  FW_PLAYER_IMPRESSION = 'fw:player:impression',

  FW_VIDEO_CHAT_RENDER = 'fw:video-chat:render',

  /**
   * @public
   * fired when subtitles / captions / transcript are toggled on
   */
  FW_PLAYER_SUBTITLES_ON = 'fw:player:subtitles-on',

  /**
   * @public
   * fired when subtitles / captions / transcript are toggled off
   */
  FW_PLAYER_SUBTITLES_OFF = 'fw:player:subtitles-off',

  FW_PROGRAMMATICPLAYER_RENDER = 'fw:programmatic-player:render',
  FW_PROGRAMMATICPLAYER_IMPRESSION = 'fw:programmatic-player:impression',

  FW_LIVE_HELPER_READY = 'fw:live-helper:ready',
  FW_LIVE_HELPER_EXTERNAL_START_CHAT = 'fw:live-helper:external-start-chat',
  FW_LIVE_HELPER_EXTERNAL_SHOW_WIDGET = 'fw:live-helper:external-show-widget',
  FW_LIVE_HELPER_EXTERNAL_START_CALL = 'fw:live-helper:external-start-call',
  FW_LIVE_HELPER_EXTERNAL_START_AVA = 'fw:live-helper:external-start-ava',

  /** Video related custom events (regardless if the video plays in a player or a thumbnail) */

  FW_VIDEO_CLICK_VIDEO = 'fw:video:click',
  FW_VIDEO_IMPRESSION = 'fw:video:impression',
  FW_VIDEO_START = 'fw:video:start',
  FW_VIDEO_RESUME = 'fw:video:resume',
  FW_VIDEO_FIRSTQUARTILE = 'fw:video:first-quartile',
  FW_VIDEO_MIDPOINT = 'fw:video:midpoint',
  FW_VIDEO_THIRDQUARTILE = 'fw:video:third-quartile',
  FW_VIDEO_COMPLETE = 'fw:video:complete',
  FW_VIDEO_SESSION_ENDED = 'fw:video:session',
  FW_VIDEO_CLICK_CTA = 'fw:video:click-cta',
  FW_VIDEO_CLICK_SHARE = 'fw:video:click-share',
  FW_VIDEO_SHARE = 'fw:video:share',
  FW_VIDEO_CLICK_EMAIL_EMBED = 'fw:video:click-email-embed',
  FW_VIDEO_CLICK_QR_CODE = 'fw:video:click-qr-code',
  FW_VIDEO_FULLSCREEN = 'fw:video:fullscreen',
  FW_VIDEO_MUTE = 'fw:video:mute',
  FW_VIDEO_UNMUTE = 'fw:video:unmute',
  FW_VIDEO_INTERACTABLE_ENGAGED_VIEW = 'fw:video:interactable-engaged-view',
  FW_VIDEO_INTERACTABLE_ENGAGED_VIEW_V2 = 'fw:video:interactable-engaged-view-v2',
  FW_VIDEO_INTERACTIVE_START = 'fw:video:interactive-start',
  FW_VIDEO_USER_PLAYED = 'fw:video:user-played',
  FW_VIDEO_USER_RESUMED = 'fw:video:user-resumed',
  FW_VIDEO_USER_PAUSED = 'fw:video:user-paused',

  FW_VIDEO_AD_START = 'fw:video:ad-start',
  FW_VIDEO_AD_SKIP = 'fw:video:ad-skip',
  FW_VIDEO_AD_END = 'fw:video:ad-end',
  FW_VIDEO_AD_START_ERROR = 'fw:video:ad-start-error',

  /** Player UI related custom events */

  // FW_PLAYER_READY = 'fw:player:ready', see eventTypeReady.ts
  FW_PLAYER_NEXT = 'fw:player:next',
  FW_PLAYER_PREV = 'fw:player:prev',
  FW_PLAYER_QUIT = 'fw:player:quit',
  FW_PLAYER_TRANSITION = 'fw:player:transition',
  FW_PLAYER_NAVIGATE = 'fw:player:navigate', // Navigate outside of player (PIP)
  FW_PLAYER_CONTENT_CHANGE = 'fw:player:content-change', // Indicate content in player has changed either by user action or automatically (e.g. autoplay to next video)
  FW_PLAYER_CTA_IMPRESSION = 'fw:player:cta:impression', // Indicate CTA has been shown

  /** Shopping (products) related custom events */

  // FW_SHOPPING_READY - see eventTypeReady.ts
  FW_SHOPPING_CONFIGURE = 'fw:shopping:configure',
  FW_SHOPPING_HYDRATECART = 'fw:shopping:hydrate-cart',
  FW_SHOPPING_HYDRATEPRODUCTS = 'fw:shopping:hydrate-products',
  FW_SHOPPING_UPDATEQUANTITY = 'fw:shopping:cart-updated',
  FW_SHOPPING_REQUESTCHECKOUT = 'fw:shopping:request-checkout',

  // Product list (shopping bag) events
  FW_SHOPPING_PRODUCT_LIST_ITEM_IMPRESSION = 'fw:shopping:product-list-item-impression',
  FW_SHOPPING_PRODUCT_LIST_ITEM_CLICKED = 'fw:shopping:product-list-item-clicked',

  // Product card and pinned product (the same components in regard of custom events)
  FW_SHOPPING_PRODUCT_CARD_IMPRESSION = 'fw:shopping:product-card-impression', // triggered on product card impression on short video or trailer
  FW_SHOPPING_PRODUCT_CARD_CLICKED = 'fw:shopping:product-card-clicked', // clicking on product cards on short video or trailer
  FW_SHOPPING_PINNED_PRODUCT_IMPRESSION = 'fw:shopping:pinned-product-impression', // impression of pinned product in livestream (product highlight)
  FW_SHOPPING_PINNED_PRODUCT_CLICKED = 'fw:shopping:pinned-product-clicked', // clicking on pinned product in livestream (product highlight)
  FW_SHOPPING_PLAYER_DECK_ADD_TO_CART_CLICK = 'fw:shopping:player-deck-add-to-cart-click', // clicking on add to cart button in player deck
  FW_SHOPPING_PLAYER_DECK_PRODUCT_LIST_EXPAND = 'fw:shopping:player-deck-product-list-expand', // expanding multi-product drawer in player deck
  FW_SHOPPING_PLAYER_DECK_PRODUCT_LIST_COLLAPSE = 'fw:shopping:player-deck-product-list-collapse', // collapsing multi-product drawer in player deck

  FW_SHOPPING_PRODUCT_CLICKED = 'fw:shopping:product-clicked', // triggered on any product click - list item, card, or highlight
  FW_SHOPPING_PDP_PRIMARY_CTA_CLICKED = 'fw:shopping:pdp-primary-cta-clicked', // clicking on the primary CTA on product details

  /** Livestream custom events */

  FW_LIVESTREAM_START_WATCH = 'fw:livestream:start-watch',
  FW_LIVESTREAM_USER_JOIN = 'fw:livestream:join',
  FW_LIVESTREAM_QUIT = 'fw:livestream:quit',
  FW_LIVESTREAM_SEND_CHAT = 'fw:livestream:send-chat',
  FW_LIVESTREAM_SEND_HEART = 'fw:livestream:send-heart',
  FW_LIVESTREAM_MODERATOR_LINK_CLICK = 'fw:livestream:click-moderator-link',

  /**
   * @deprecated use FW_LIVESTREAM_INTERACTION_SUBMIT_ANSWER instead
   */
  FW_LIVESTREAM_INTERACTION_SUBMIT_ANSWER_LEGACY = 'fw:video:submit-answer',

  /**
   * @deprecated use FW_LIVESTREAM_INTERACTION_SUBMIT_CHOICE instead
   */
  FW_LIVESTREAM_INTERACTION_SUBMIT_CHOICE_LEGACY = 'fw:video:submit-choice',

  FW_LIVESTREAM_INTERACTION_SUBMIT_ANSWER = 'fw:interaction:submit-answer',
  FW_LIVESTREAM_INTERACTION_SUBMIT_CHOICE = 'fw:interaction:submit-choice',
  FW_LIVESTREAM_INTERACTION_GIVEAWAY_WINNER_CHOSEN = 'fw:interaction:giveaway:winner-chosen',

  /** Ads related custom events */

  FW_ADS_UNFILLED_AD = 'fw:ads:unfilled_ad',
  FW_ADS_FILLED_AD = 'fw:ads:filled_ad',
}
